import { LogSentryException, ServiceError } from "@shapr3d/frontend-lib";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "../config";
import { UMSrequestClient } from "../stores/requestClient";
import { ServiceErrorCode } from "../types";
import { accountQueryKey } from "./useAccount";
import { activeSubscriptionQueryKey } from "./useTeamActiveSubscription";
import { TeamMembersQueryKey, TeamSubscriptionsQueryKey } from "./useTeams";

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const INITIAL_VERIFY_DELAY_MS = 1500;
const PAYMENT_PROCESSING_RETRY_DELAYS_MS = [2000, 4000, 8000];

const verifyPaddleBillingTransaction = async (transactionId: string) => {
  await sleep(INITIAL_VERIFY_DELAY_MS);

  let lastError: ServiceError | undefined;

  for (let attempt = 0; attempt <= PAYMENT_PROCESSING_RETRY_DELAYS_MS.length; attempt++) {
    if (attempt > 0) {
      await sleep(PAYMENT_PROCESSING_RETRY_DELAYS_MS[attempt - 1]);
    }
    try {
      await UMSrequestClient.authenticatedRequest({
        method: "POST",
        url: `/checkout/paddle-billing/verify`,
        body: {
          transactionId,
        },
      });
      return;
    } catch (error) {
      if (
        error instanceof ServiceError &&
        error.hasCode(ServiceErrorCode.PaymentProcessingFailed)
      ) {
        lastError = error;
      } else {
        throw error;
      }
    }
  }

  throw lastError;
};

export const useVerifyPaddleBillingTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation<void, Error | ServiceError, string>({
    mutationKey: [...queryKeys.authenticatedQueryKey, "verifyPaddleBillingTransaction"],
    mutationFn: verifyPaddleBillingTransaction,
    onError: (error) => {
      LogSentryException({
        messagePrefix: "Paddle Billing transaction verification failed",
        error,
        level: "error",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: accountQueryKey });
      queryClient.invalidateQueries({ queryKey: TeamSubscriptionsQueryKey, refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: TeamMembersQueryKey, refetchType: "all" });
      queryClient.invalidateQueries({ queryKey: activeSubscriptionQueryKey });
    },
  });
};
